<template>
  <div>
    <ExPickerPopUp
      ref="pickerPopup"
      :value="dialog"
      :htmlInfo="info"
      icon="currency_icon"
      :applyBorderClass="true"
      actionLabel="Add Operating Currency"
      :dropdownCurrencyList="activeDropdownCurrencyList"
      :operating-catalog-currency-ids="operatingCatalogCurrencyIds"
      @input="emit('onClose')"
      @onAction="handleAddCurrency"
    >
    </ExPickerPopUp>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import ExPickerPopUp from "@components/common/ExPickerPopUp.vue";
import {
  OPERATING_ERROR_CODES,
  getOperatingErrorMessage,
} from "@components/constants/operatingErrorCodes";

const props = defineProps({
  dialog: {
    type: Boolean,
    default: false,
  },
  dropdownCurrencyList: {
    type: Array,
    default: () => [],
  },
  operatingCatalogCurrencyIds: {
    type: Array,
    default: () => [],
  },
  /**
   * Backend error code to surface inline (e.g. err.response.data.code after a
   * 400 from the add-operating-currency endpoint). Mapped to copy via the
   * shared operatingErrorCodes map; empty clears the message.
   */
  errorCode: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["onClose", "onAddCurrency"]);

const pickerPopup = ref(null);

const info = computed(() => "You are about to add an Operating Currency.");

// AC: picker only lists Currency.active = true (inactive currencies must not
// appear — CURRENCY_NOT_AVAILABLE is for API edge cases, not selectable rows).
const activeDropdownCurrencyList = computed(() =>
  (props.dropdownCurrencyList || []).filter((currency) => {
    if (!currency || typeof currency !== "object") return false;
    if (currency.active === false) return false;
    if (currency.active === true) return true;
    // Legacy catalog rows without `active` stay visible (treated as available).
    return currency.active == null;
  })
);

const setError = (message) => {
  pickerPopup.value?.setErrorMessage(message);
};

const handleAddCurrency = (event) => {
  if (event.disabled) {
    setError(
      getOperatingErrorMessage(OPERATING_ERROR_CODES.CURRENCY_ALREADY_ADDED)
    );
    return;
  }
  if (event.active === false) {
    setError(
      getOperatingErrorMessage(OPERATING_ERROR_CODES.CURRENCY_NOT_AVAILABLE)
    );
    return;
  }
  setError("");
  emit("onAddCurrency", event);
};

watch(
  () => props.dialog,
  (isOpen) => {
    if (!isOpen) {
      setError("");
    }
  }
);

watch(
  () => props.errorCode,
  (code) => {
    setError(code ? getOperatingErrorMessage(code) : "");
  }
);

defineExpose({ handleAddCurrency, activeDropdownCurrencyList });
</script>

<style scoped>
.error-message {
  color: #f04438;
  font-size: 14px;
  margin-top: 8px;
  text-align: left;
  font-family: Inter;
}
</style>
